import React, { useEffect, useId, useRef, useState } from 'react';
import {
    LayoutDashboard, Shield, LogOut, Settings, Users, HardHat, ShieldCheck,
    Briefcase, Edit, FolderCog, Palmtree, ClipboardCheck, ClipboardList, FileText,
    Map, Shuffle, BarChart3, MapPin, Image as ImageIcon, ArrowRightLeft, ListChecks, UserCog,
    Building2, ArrowLeft, Archive, CheckSquare, Download, History, Landmark, X, Printer, Database, PanelLeftClose, PanelLeftOpen, Contact, Receipt, Target, GraduationCap, Smartphone, BookOpenCheck, Library, Package, UserSearch
} from 'lucide-react';
import { UserProfile, AppView } from '../types';
import { ADMIN_BEREICHE, ADMIN_BEREICH_VIEWS, type AdminBereichView } from './admin/adminBereiche';
import { useIsMobile } from '../hooks/useIsMobile';
import { appVariante } from '../services/nativePlatform';
import { ansichtInAppErlaubt, umfangBeschraenkt } from '../services/appUmfang';
import { appReleaseVorhanden } from '../services/appRelease';
import { useRechte } from '../hooks/useRechte';
import { ansichtErlaubt } from '../services/rechte/ansichtRechte';
import { useGlassDesign, useProfileDesign } from '../design/glassMode';

interface Props {
    view: AppView;
    setView: (view: AppView) => void;
    userProfile: UserProfile | null;
    appMode: 'sigeko' | 'hse';
    onLogout: () => void;
    mobileMenuOpen: boolean;
    setMobileMenuOpen: (open: boolean) => void;
    onOpenProfile: () => void;
    onOpenHolidayPlanner: () => void;
    activeSection?: string;
    onSectionChange?: (section: any) => void;
    onBackToDashboard?: () => void;
    /**
     * SaveStatus plus 'read-only': der Lesemodus ist kein Zustand der
     * Offline-Queue, braucht aber dieselbe Anzeige — hier sucht der Nutzer
     * sonst vergeblich nach "Gespeichert".
     */
    saveStatus?: import('../hooks/useOfflineSync').SaveStatus | 'read-only';
    currentProjectName?: string;
    incomingTransfersCount?: number;
    leadsDueCount?: number;
    /** Seite `#/app` oeffnen (Download bzw. App-Info). Ohne: kein Menueeintrag. */
    onOpenAppSeite?: () => void;
    /** Steht die Seite `#/app` gerade offen? */
    appSeiteAktiv?: boolean;
    /**
     * Häkchen „SiFa" des angemeldeten Nutzers (docs/sifa-vorlagen-plan.md
     * §3f): schaltet den Menüpunkt „Unterweisungen" auch ohne Admin-Rolle
     * frei — ODER das Recht `sifa.vorlagen`. Nur Anzeige — ob geschrieben
     * werden darf, entscheidet RLS.
     */
    istSifa?: boolean;
    /** Offene Materialmeldungen fürs Abzeichen am Menüpunkt „Material“ (useMaterialOffen). */
    materialOffenCount?: number;
    /** Neue Bewerbungen fürs Abzeichen am Menüpunkt „Talentpool“ (useTalentpoolNeu). */
    talentpoolNeuCount?: number;
}

/**
 * Ansichten, unter denen das Hauptmenü steht. Die Verwaltungsbereiche kommen
 * aus ihrer Tabelle — ein neuer Bereich dort hat sonst eine leere Leiste.
 */
const HAUPTMENUE_ANSICHTEN: readonly AppView[] = [
    'dashboard', 'projects', 'tasks', 'global-map',
    'crm', 'crm-map', 'leads', 'quotes', 'invoices', 'erp-cockpit', 'freelancers',
    'sifa-vorlagen', 'lernzentrum', 'material', 'talentpool',
    ...ADMIN_BEREICH_VIEWS,
];

/** Ein Eintrag im Hauptmenü; `sichtbar` bündelt Recht, Rolle und Gerät. */
interface MenueEintrag {
    id: string;
    label: string;
    icon: React.ReactElement;
    sichtbar: boolean;
    onClick: () => void;
    isActive?: boolean;
    badge?: number;
    tutorialAnker?: string;
}

interface MenueAbschnitt {
    titel: string;
    eintraege: MenueEintrag[];
}

// Build-Stempel: macht ein veraltetes Deployment sofort sichtbar, ohne dass
// jemand die Konsole oeffnen muss. __APP_BUILD__ wird von vite.config.ts
// eingesetzt; unter vitest laeuft der Code ohne define, daher der typeof-Riegel.
const BUILD_INFO: { commit: string; builtAt: string } =
    typeof __APP_BUILD__ !== 'undefined' ? __APP_BUILD__ : { commit: 'dev', builtAt: '' };

const BUILD_STAMP = (() => {
    const at = Date.parse(BUILD_INFO.builtAt);
    if (!Number.isFinite(at)) return `Build ${BUILD_INFO.commit}`;
    const when = new Date(at).toLocaleString('de-DE', {
        day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit',
    });
    return `Build ${BUILD_INFO.commit} · ${when}`;
})();

interface NavItemProps {
    icon: React.ReactNode;
    label: string;
    isActive?: boolean;
    onClick?: () => void;
    badge?: number;
    collapsed?: boolean;
    /**
     * `data-tutorial`-Anker am <button> (Lernzentrum, Plan 1.1). Reines
     * Attribut: kein Verhalten, kein Zustand, keine Fachlogik. Die Tour
     * (Paket B) und der Pipeline-Roboter finden das Element ueber
     * `querySelector('[data-tutorial="…"]')`; die Namen stehen im Register
     * (`src/tutorial/register.ts`) und werden nie umbenannt.
     */
    tutorialAnker?: string;
}

const NavItem: React.FC<NavItemProps> = ({ icon, label, isActive, onClick, badge, collapsed, tutorialAnker }) => (
    <div className="relative group">
        {/* Aktiv-Schiene. Sie sitzt ausserhalb des Knopfes, damit sie ueber die
            volle Zeilenhoehe laufen kann, und waechst beim Wechsel heran — die
            Auswahl wandert sichtbar, statt umzuspringen. */}
        <span
            aria-hidden
            className={`absolute left-0 top-1/2 -translate-y-1/2 w-[3px] rounded-r-full bg-blue-600
                transition-all duration-300 ease-[cubic-bezier(0.16,1,0.3,1)]
                ${isActive ? 'h-5 opacity-100' : 'h-0 opacity-0'}`}
        />
        <button
            type="button"
            onClick={onClick}
            data-tutorial={tutorialAnker}
            aria-current={isActive ? 'page' : undefined}
            title={collapsed ? label : undefined}
            className={`w-full flex items-center gap-3 relative
                transition-[background-color,color,box-shadow] duration-200 ease-[cubic-bezier(0.16,1,0.3,1)]
                ${collapsed ? 'justify-center px-0 py-2.5 mx-auto w-10 h-10 rounded-xl' : 'px-3 py-2 rounded-xl'}
                ${isActive
                    ? 'bg-blue-50 text-blue-700 font-semibold shadow-xs ring-1 ring-inset ring-blue-100'
                    : 'text-slate-600 hover:bg-white/70 hover:text-slate-900 hover:shadow-xs'
                }
            `}
        >
            <span className={`shrink-0 transition-colors duration-200 ${isActive ? 'text-blue-600' : 'text-slate-400 group-hover:text-slate-700'}`}>
                {React.cloneElement(icon as React.ReactElement<any>, { className: `${collapsed ? 'w-5 h-5' : 'w-4 h-4'}` })}
            </span>
            {!collapsed && <span className="text-[13px] truncate">{label}</span>}
            {badge !== undefined && badge > 0 && (
                <span className={`bg-red-600 text-white text-[10px] font-bold tabular rounded-full ring-2 ring-white/80 ${collapsed ? 'absolute -top-1 -right-1 min-w-4 h-4 px-1 flex items-center justify-center' : 'ml-auto px-1.5 py-0.5'}`}>
                    {badge}
                </span>
            )}
        </button>
        {/* Tooltip when collapsed */}
        {collapsed && (
            <div className="absolute left-full top-1/2 -translate-y-1/2 ml-3 px-2.5 py-1.5 bg-slate-900 text-white text-xs font-semibold rounded-lg whitespace-nowrap opacity-0 -translate-x-1 pointer-events-none group-hover:opacity-100 group-hover:translate-x-0 transition-all duration-200 ease-[cubic-bezier(0.16,1,0.3,1)] z-[100] shadow-lg">
                {label}
                <div className="absolute right-full top-1/2 -translate-y-1/2 border-4 border-transparent border-r-slate-900" />
            </div>
        )}
    </div>
);

const SectionTitle: React.FC<{ children: React.ReactNode; collapsed?: boolean }> = ({ children, collapsed }) => {
    if (collapsed) return <div className="my-2 mx-3 border-t border-slate-200" />;
    return <div className="px-3 pt-5 pb-1.5 text-[10px] font-semibold text-slate-500 uppercase tracking-[0.12em]">{children}</div>;
};

interface KontoEintragProps {
    icon: React.ReactNode;
    label: string;
    onClick: () => void;
    isActive?: boolean;
    /** Abmelden: rot beim Hover, damit es sich vom Rest absetzt. */
    gefahr?: boolean;
}

const KontoEintrag: React.FC<KontoEintragProps> = ({ icon, label, onClick, isActive, gefahr }) => (
    <button
        type="button"
        role="menuitem"
        onClick={onClick}
        aria-current={isActive ? 'page' : undefined}
        className={`w-full flex items-center gap-2.5 px-2.5 py-2 rounded-lg text-[13px] text-left outline-none transition-colors duration-150
            ${gefahr
                ? 'text-slate-600 hover:bg-red-50 hover:text-red-600 focus-visible:bg-red-50 focus-visible:text-red-600'
                : isActive
                    ? 'bg-blue-50 text-blue-700 font-semibold'
                    : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900 focus-visible:bg-slate-100 focus-visible:text-slate-900'
            }`}
    >
        <span className="shrink-0 opacity-70">{icon}</span>
        <span className="truncate">{label}</span>
    </button>
);

export const Sidebar: React.FC<Props> = ({
    view, setView, userProfile, appMode, onLogout,
    mobileMenuOpen, setMobileMenuOpen, onOpenProfile, onOpenHolidayPlanner,
    activeSection, onSectionChange, onBackToDashboard, saveStatus, currentProjectName,
    incomingTransfersCount = 0, leadsDueCount = 0, onOpenAppSeite, appSeiteAktiv = false, istSifa = false,
    materialOffenCount = 0, talentpoolNeuCount = 0
}) => {
    const [collapsed, setCollapsed] = useState(false);
    const isAdmin = userProfile?.role === 'admin';
    const isSuperAdmin = userProfile?.role === 'super_admin';
    const isHse = appMode === 'hse';
    const isMobile = useIsMobile();
    // Rechte (Rollensystem): ohne Migration die der Systemrolle — dann ist
    // das Menü genau das von heute.
    const rechte = useRechte(userProfile?.role);
    const darf = (v: AppView) => ansichtErlaubt(v, rechte, userProfile?.role, { istSifaFachkraft: istSifa });
    // Optik „Glas": die Wahl aus dem Profil uebernehmen (die Leiste haengt
    // fuer jeden angemeldeten Nutzer, auch am Handy als Schublade). Am PC
    // dann schwebende Icon-Leiste wie im Vorbild — standardmaessig schmal,
    // der Einklapp-Knopf klappt sie hier auf.
    // Eigener Zustand, damit ein Wechsel der Optik die klassische Leiste
    // nicht eingeklappt zuruecklaesst.
    useProfileDesign(userProfile?.id, userProfile?.settings);
    const glass = useGlassDesign() && !isMobile;
    const [railOpen, setRailOpen] = useState(false);
    const c = (glass ? !railOpen : collapsed) && !isMobile; // Only collapse on desktop
    // Handy-App: nur Eintraege aus APP_ANSICHTEN (docs/android-app-plan.md
    // 1.4, Vertrag E11) — derselbe Mechanismus wie `!isMobile`. Tablet-App
    // und Browser: unbeschraenkt, Menue wie bisher.
    const variante = appVariante();
    const handyUmfang = umfangBeschraenkt(variante);
    const zeige = (v: AppView) => ansichtInAppErlaubt(v, variante);
    // Seite `#/app`: im Browser der Download, in beiden App-Varianten Version
    // und Update-Stand. Nur intern — Freelancer sehen dieses
    // Menue ohnehin nie (eigenes Portal), die Bedingung sichert es trotzdem.
    // Im Browser erst, wenn eine Release-Info lesbar ist (Vertrag E19): vorher
    // fuehrte der Eintrag auf eine leere Download-Seite. In der App immer.
    const appEintragMoeglich = !!onOpenAppSeite && !!userProfile && userProfile.role !== 'freelancer';
    const [releaseVorhanden, setReleaseVorhanden] = useState(false);
    useEffect(() => {
        if (variante || !appEintragMoeglich) return;
        let aktiv = true;
        appReleaseVorhanden()
            .then((ja) => { if (aktiv) setReleaseVorhanden(ja); })
            .catch(() => undefined);
        return () => { aktiv = false; };
    }, [variante, appEintragMoeglich]);
    const appEintragSichtbar = appEintragMoeglich && (!!variante || releaseVorhanden);

    // Konto-Menü am Avatar: Profil, App, Hilfe und Abmelden. Sie stehen nicht
    // mehr in der Navigation — dort sind nur noch Arbeitsbereiche.
    const [kontoOffen, setKontoOffen] = useState(false);
    const kontoRef = useRef<HTMLDivElement>(null);
    const kontoKnopfRef = useRef<HTMLButtonElement>(null);
    const kontoMenueId = useId();
    useEffect(() => {
        if (!kontoOffen) return;
        const draussen = (e: PointerEvent) => {
            if (!kontoRef.current?.contains(e.target as Node)) setKontoOffen(false);
        };
        const taste = (e: KeyboardEvent) => {
            if (e.key !== 'Escape') return;
            setKontoOffen(false);
            kontoKnopfRef.current?.focus();
        };
        document.addEventListener('pointerdown', draussen);
        document.addEventListener('keydown', taste);
        // Fokus auf den ersten Eintrag: per Tastatur geöffnet, greifen Pfeile
        // und Enter sofort.
        kontoRef.current?.querySelector<HTMLElement>('[role="menuitem"]')?.focus();
        return () => {
            document.removeEventListener('pointerdown', draussen);
            document.removeEventListener('keydown', taste);
        };
    }, [kontoOffen]);
    const kontoPfeile = (e: React.KeyboardEvent<HTMLDivElement>) => {
        if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return;
        e.preventDefault();
        const punkte = Array.from(e.currentTarget.querySelectorAll<HTMLElement>('[role="menuitem"]'));
        const n = punkte.length;
        if (n === 0) return;
        const runter = e.key === 'ArrowDown';
        const i = punkte.indexOf(document.activeElement as HTMLElement);
        punkte[i < 0 ? (runter ? 0 : n - 1) : (i + (runter ? 1 : -1) + n) % n].focus();
    };

    const companyLogo = userProfile?.company_logo;
    const companyName = userProfile?.company_name;
    const isProjectContext = ['editor', 'project-hub', 'module-vorank', 'module-unterlage', 'module-sigeko-inspection'].includes(view);

    const nav = (v: AppView) => { setView(v); setMobileMenuOpen(false); };
    // Solange die Seite `#/app` offen ist, ist kein Menuepunkt darunter „aktiv"
    // — markiert ist dann nur ihr eigener Eintrag.
    const aktiv = (v: AppView) => !appSeiteAktiv && view === v;
    const sec = (s: string) => { onSectionChange?.(s); setMobileMenuOpen(false); };
    const kontoWahl = (aktion: () => void) => () => { setKontoOffen(false); setMobileMenuOpen(false); aktion(); };

    // ── Menu Renderers ──

    // Hauptmenü nach Arbeitsbereichen statt nach Rolle (Menü-Umbau
    // 19.10.2026). Admin und Super-Admin haben jedes Recht und sehen alles;
    // alle anderen nur, wofür `darf` gilt — dieselbe Regel wie die
    // Ansichts-Wache in App.tsx. Ein Abschnitt ohne sichtbaren Eintrag fällt
    // samt Titel weg. Ausser „Arbeit" und „Material" steht alles nur am
    // Schreibtisch, wie bisher Vertrieb und Verwaltung.
    const renderDashboardMenu = () => {
        const istAdminRolle = isAdmin || isSuperAdmin;
        const amSchreibtisch = !isMobile && !handyUmfang;
        // Der Super-Admin betreut Firmen, nicht Projekte: am Schreibtisch ohne
        // Tagesgeschäft und Urlaubsplaner.
        const ohneTagesgeschaeft = isSuperAdmin && amSchreibtisch;
        const verwaltung = istAdminRolle && amSchreibtisch;
        // Menüpunkt und Seitentitel der AdminView kommen aus derselben Tabelle.
        const bereich = (v: AdminBereichView, icon: React.ReactElement, label = ADMIN_BEREICHE[v].titel): MenueEintrag => (
            { id: v, label, icon, sichtbar: verwaltung, isActive: aktiv(v), onClick: () => nav(v) }
        );

        const arbeit: MenueAbschnitt = {
            titel: 'Arbeit',
            eintraege: [
                { id: 'dashboard', tutorialAnker: 'sidebar.dashboard', label: 'Dashboard', icon: <LayoutDashboard />, sichtbar: !ohneTagesgeschaeft && zeige('dashboard'), isActive: aktiv('dashboard'), onClick: () => nav('dashboard') },
                { id: 'projects', tutorialAnker: 'sidebar.projekte', label: 'Projekte', icon: <FolderCog />, sichtbar: !ohneTagesgeschaeft && zeige('projects'), isActive: aktiv('projects'), onClick: () => nav('projects'), badge: incomingTransfersCount },
                { id: 'tasks', tutorialAnker: 'sidebar.aufgaben', label: 'Aufgaben', icon: <CheckSquare />, sichtbar: !ohneTagesgeschaeft && zeige('tasks'), isActive: aktiv('tasks'), onClick: () => nav('tasks') },
                // Projekt- und Kundenkarte teilen sich einen Menüpunkt; umgeschaltet wird in der Ansicht.
                // Auch beim Super-Admin: die Kundenkarte stand bei ihm früher unter
                // Vertrieb, und dies ist jetzt ihr einziger Weg.
                { id: 'karte', label: 'Karte', icon: <Map />, sichtbar: zeige('global-map'), isActive: aktiv('global-map') || aktiv('crm-map'), onClick: () => nav('global-map') },
            ],
        };
        const vertrieb: MenueAbschnitt = {
            titel: 'Vertrieb',
            eintraege: [
                { id: 'crm', label: 'Kunden', icon: <Contact />, sichtbar: amSchreibtisch && darf('crm'), isActive: aktiv('crm'), onClick: () => nav('crm') },
                { id: 'leads', label: 'Neukunden', icon: <Target />, sichtbar: amSchreibtisch && darf('leads'), isActive: aktiv('leads'), onClick: () => nav('leads'), badge: leadsDueCount },
                { id: 'quotes', label: 'Angebote', icon: <FileText />, sichtbar: amSchreibtisch && darf('quotes'), isActive: aktiv('quotes'), onClick: () => nav('quotes') },
            ],
        };
        const finanzen: MenueAbschnitt = {
            titel: 'Finanzen',
            eintraege: [
                { id: 'invoices', label: 'Rechnungen', icon: <Receipt />, sichtbar: amSchreibtisch && darf('invoices'), isActive: aktiv('invoices'), onClick: () => nav('invoices') },
                bereich('admin-open-positions', <ClipboardList />),
                // KPI (Admin) und Steuerung (Recht `erp.steuerung`) unter einem
                // Punkt; der Admin landet bei den KPI und schaltet in der Ansicht um.
                { id: 'auswertungen', label: 'Auswertungen', icon: <BarChart3 />, sichtbar: amSchreibtisch && (istAdminRolle || darf('erp-cockpit')), isActive: aktiv('project-management') || aktiv('erp-cockpit'), onClick: () => nav(istAdminRolle ? 'project-management' : 'erp-cockpit') },
            ],
        };
        const team: MenueAbschnitt = {
            titel: 'Team',
            eintraege: [
                // Urlaubsplaner ist ein Dialog, keine Ansicht — auch im
                // Handy-Browser, in der Handy-App nicht Teil des Umfangs (E5).
                { id: 'urlaub', label: 'Urlaubsplaner', icon: <Palmtree />, sichtbar: !handyUmfang && !ohneTagesgeschaeft, onClick: () => { onOpenHolidayPlanner(); setMobileMenuOpen(false); } },
                bereich('admin-substitutions', <ArrowRightLeft />),
                { id: 'freelancers', label: 'Freelancer', icon: <HardHat />, sichtbar: amSchreibtisch && darf('freelancers'), isActive: aktiv('freelancers'), onClick: () => nav('freelancers') },
                // Talentpool (docs/talentpool-plan.md): Bewerbungen von der
                // Website, nur mit Recht `talentpool.verwalten` und nur am
                // Schreibtisch; das Abzeichen zählt die neuen.
                { id: 'talentpool', label: 'Talentpool', icon: <UserSearch />, sichtbar: amSchreibtisch && darf('talentpool'), isActive: aktiv('talentpool'), onClick: () => nav('talentpool'), badge: talentpoolNeuCount },
            ],
        };
        const vorlagen: MenueAbschnitt = {
            titel: 'Vorlagen',
            eintraege: [
                // SiFa-Vorlagen (docs/sifa-vorlagen-plan.md §3f): auch für die
                // Fachkraft ohne Admin-Rolle (Häkchen „SiFa" oder Recht).
                { id: 'sifa-vorlagen', label: 'Unterweisungen', icon: <BookOpenCheck />, sichtbar: amSchreibtisch && darf('sifa-vorlagen'), isActive: aktiv('sifa-vorlagen'), onClick: () => nav('sifa-vorlagen') },
                bereich('admin-vorlagen', <ListChecks />),
            ],
        };
        const verwaltungAbschnitt: MenueAbschnitt = {
            titel: 'Verwaltung',
            eintraege: [
                bereich('admin-projects', <FolderCog />),
                bereich('users', <Users />, isSuperAdmin ? 'Benutzer & Firmen' : undefined),
                bereich('admin-settings', <Settings />),
                // Materialverwaltung (docs/materialverwaltung-plan.md): nach
                // Recht, nicht nach Admin-Rolle, und auch unterwegs — der
                // Beschaffer setzt „Bestellt“ dort, wo er die Glocke liest.
                // Wer nur die Rolle „Beschaffung“ hat, sieht den Abschnitt
                // mit diesem einen Eintrag.
                { id: 'material', label: 'Material', icon: <Package />, sichtbar: zeige('material') && darf('material'), isActive: aktiv('material'), onClick: () => nav('material'), badge: materialOffenCount },
                bereich('admin', <Shield />),
            ],
        };
        // Beim Super-Admin ist die Verwaltung die eigentliche Arbeit — sie steht oben.
        const abschnitte = isSuperAdmin
            ? [verwaltungAbschnitt, arbeit, vertrieb, finanzen, team, vorlagen]
            : [arbeit, vertrieb, finanzen, team, vorlagen, verwaltungAbschnitt];

        return (
            <>
                {abschnitte.map(({ titel, eintraege }) => {
                    const sichtbar = eintraege.filter(e => e.sichtbar);
                    if (sichtbar.length === 0) return null;
                    return (
                        <React.Fragment key={titel}>
                            <SectionTitle collapsed={c}>{titel}</SectionTitle>
                            {sichtbar.map(e => (
                                <NavItem key={e.id} collapsed={c} tutorialAnker={e.tutorialAnker} icon={e.icon} label={e.label} isActive={e.isActive} onClick={e.onClick} badge={e.badge} />
                            ))}
                        </React.Fragment>
                    );
                })}
            </>
        );
    };

    const renderProjectHubMenu = () => (
        <>
            <SectionTitle collapsed={c}>Projekt-Zentrale</SectionTitle>
            <NavItem collapsed={c} icon={<LayoutDashboard />} label="Übersicht" isActive onClick={() => setMobileMenuOpen(false)} />
        </>
    );

    const renderSiGeKoMenu = () => (
        <>
            <SectionTitle collapsed={c}>Begehungsbericht</SectionTitle>
            <NavItem collapsed={c} icon={<ClipboardCheck />} label="Mängel / Festst." isActive={activeSection === 'items'} onClick={() => sec('items')} />
            <NavItem collapsed={c} icon={<Users />} label="Beteiligte" isActive={activeSection === 'contacts'} onClick={() => sec('contacts')} />
            <NavItem collapsed={c} icon={<CheckSquare />} label="Checklisten" isActive={activeSection === 'checklists'} onClick={() => sec('checklists')} />
            <NavItem collapsed={c} icon={<Map />} label="Pläne & Verortung" isActive={activeSection === 'plans'} onClick={() => sec('plans')} />
            <SectionTitle collapsed={c}>Analyse</SectionTitle>
            <NavItem collapsed={c} icon={<Shuffle />} label="Gefährdungs-Matrix" isActive={activeSection === 'interactions'} onClick={() => sec('interactions')} />
            <NavItem collapsed={c} icon={<BarChart3 />} label="Statistik" isActive={activeSection === 'analysis'} onClick={() => sec('analysis')} />
            <NavItem collapsed={c} icon={<MapPin />} label="Standort" isActive={activeSection === 'project-map'} onClick={() => sec('project-map')} />
            <SectionTitle collapsed={c}>Historie</SectionTitle>
            <NavItem collapsed={c} icon={<CheckSquare />} label="Erledigte Mängel" isActive={activeSection === 'history'} onClick={() => sec('history')} />
            <NavItem collapsed={c} icon={<History />} label="Berichts-Archiv" isActive={activeSection === 'archive'} onClick={() => sec('archive')} />
            <NavItem collapsed={c} icon={<Download />} label="PDF Export" isActive={activeSection === 'export'} onClick={() => sec('export')} />
            <NavItem collapsed={c} icon={<Settings />} label="Einstellungen" isActive={activeSection === 'settings'} onClick={() => sec('settings')} />
        </>
    );

    const renderUnterlageMenu = () => (
        <>
            <SectionTitle collapsed={c}>Unterlage (RAB 32)</SectionTitle>
            <NavItem collapsed={c} icon={<Database />} label="Stammdaten" isActive={activeSection === 'project'} onClick={() => sec('project')} />
            <NavItem collapsed={c} icon={<FileText />} label="Maßnahmenkatalog" isActive={activeSection === 'catalog'} onClick={() => sec('catalog')} />
            <NavItem collapsed={c} icon={<Library />} label="Mitgeltende Unterlagen" isActive={activeSection === 'mitgeltend'} onClick={() => sec('mitgeltend')} />
            <NavItem collapsed={c} icon={<ImageIcon />} label="Bild- & Risikoanalyse" isActive={activeSection === 'appendix'} onClick={() => sec('appendix')} />
            <SectionTitle collapsed={c}>Ausgabe</SectionTitle>
            <NavItem collapsed={c} icon={<Printer />} label="Vorschau & Export" isActive={activeSection === 'print'} onClick={() => sec('print')} />
        </>
    );

    const renderVorankMenu = () => (
        <>
            <SectionTitle collapsed={c}>Vorankündigung</SectionTitle>
            <NavItem collapsed={c} icon={<Edit />} label="Formular" isActive={activeSection === 'editor'} onClick={() => sec('editor')} />
            <NavItem collapsed={c} icon={<FileText />} label="Vorschau" isActive={activeSection === 'preview'} onClick={() => sec('preview')} />
            <SectionTitle collapsed={c}>Verwaltung</SectionTitle>
            <NavItem collapsed={c} icon={<Landmark />} label="Behörden" isActive={activeSection === 'authorities'} onClick={() => sec('authorities')} />
            <NavItem collapsed={c} icon={<Archive />} label="Archiv" isActive={activeSection === 'archive'} onClick={() => sec('archive')} />
        </>
    );

    const renderEditorMenu = () => (
        <>
            <SectionTitle collapsed={c}>Berichts-Inhalte</SectionTitle>
            <NavItem collapsed={c} icon={<ClipboardCheck />} label={isHse ? "Issues / Findings" : "Mängel & Festst."} isActive={activeSection === 'items'} onClick={() => sec('items')} />
            <NavItem collapsed={c} icon={<Briefcase />} label="Checklisten" isActive={activeSection === 'checklist'} onClick={() => sec('checklist')} />
            <SectionTitle collapsed={c}>Analyse & Pläne</SectionTitle>
            <NavItem collapsed={c} icon={<Map />} label="Standort / Karte" isActive={activeSection === 'project-map'} onClick={() => sec('project-map')} />
            <NavItem collapsed={c} icon={<ImageIcon />} label="Baupläne & Pins" isActive={activeSection === 'safety-points'} onClick={() => sec('safety-points')} />
            <NavItem collapsed={c} icon={<Shuffle />} label="Gefährdungs-Matrix" isActive={activeSection === 'interactions'} onClick={() => sec('interactions')} />
            <NavItem collapsed={c} icon={<Briefcase />} label="Erledigte Mängel" isActive={activeSection === 'history'} onClick={() => sec('history')} />
            <NavItem collapsed={c} icon={<BarChart3 />} label="Statistik" isActive={activeSection === 'analysis'} onClick={() => sec('analysis')} />
            <SectionTitle collapsed={c}>Sonstiges</SectionTitle>
            <NavItem collapsed={c} icon={<Archive />} label="Berichts-Archiv" isActive={activeSection === 'archive'} onClick={() => sec('archive')} />
            <NavItem collapsed={c} icon={<Settings />} label="Einstellungen" isActive={activeSection === 'settings'} onClick={() => sec('settings')} />
        </>
    );

    return (
        <>
            {/* Mobile Overlay */}
            {mobileMenuOpen && <div className="fixed inset-0 bg-black/60 animate-fade-in z-50 md:hidden backdrop-blur-sm" onClick={() => setMobileMenuOpen(false)} />}

            <aside data-tutorial="sidebar.leiste" className={`
                fixed inset-y-0 left-0 z-[60] flex flex-col transition-all duration-300 ease-[cubic-bezier(0.25,0.8,0.25,1)]
                ${mobileMenuOpen ? 'translate-x-0' : '-translate-x-full'} md:relative md:translate-x-0
                ${c ? 'w-[60px]' : 'w-[260px]'}
                glass shadow-[4px_0_24px_-12px_rgba(15,23,42,0.18)]
                ${glass ? 'glass-rail' : ''}
            `}>

                {/* ── LOGO / HEADER ── */}
                <div className={`shrink-0 border-b border-slate-200/60 ${c ? 'px-2 py-3' : 'px-4 py-4'}`}>
                    {/* Mobile close */}
                    <button onClick={() => setMobileMenuOpen(false)} className="md:hidden absolute top-3 right-3 p-1.5 text-slate-400 hover:text-slate-600 bg-slate-100 rounded-lg">
                        <X className="w-4 h-4" />
                    </button>

                    {isProjectContext ? (
                        /* Project context header */
                        <div className={`${c ? 'flex flex-col items-center gap-2' : ''}`}>
                            <button onClick={onBackToDashboard} className={`flex items-center gap-2 text-slate-400 hover:text-blue-600 transition-colors group ${c ? 'p-1.5 rounded-lg hover:bg-blue-50' : 'text-xs font-bold mb-3'}`}>
                                <ArrowLeft className={`${c ? 'w-5 h-5' : 'w-3.5 h-3.5'}`} />
                                {!c && <span className="uppercase tracking-wider text-[11px]">{view === 'project-hub' ? 'Dashboard' : 'Zentrale'}</span>}
                            </button>
                            {!c && (
                                <div className="flex items-start gap-2.5">
                                    <div className="p-2 rounded-lg bg-blue-50 border border-blue-100 shrink-0">
                                        <Building2 className="w-4 h-4 text-blue-500" />
                                    </div>
                                    <div className="min-w-0">
                                        <span className="text-[10px] text-blue-500 font-bold uppercase tracking-wider">
                                            {view === 'project-hub' ? 'Projekt' : view === 'module-vorank' ? 'Vorankündigung' : view === 'module-unterlage' ? 'Unterlage' : 'Begehung'}
                                        </span>
                                        <h2 className="text-sm font-bold text-slate-800 leading-tight truncate" title={currentProjectName}>{currentProjectName || 'Projekt'}</h2>
                                    </div>
                                </div>
                            )}
                        </div>
                    ) : (
                        /* Dashboard header with logo */
                        <div className={`flex items-center ${c ? 'justify-center' : 'gap-3'}`}>
                            {c ? (
                                /* Collapsed: small icon */
                                companyLogo ? (
                                    <img src={companyLogo} alt="" className="w-8 h-8 rounded-lg object-contain" />
                                ) : (
                                    <div className={`w-8 h-8 rounded-xl bg-gradient-to-br ${isHse ? 'from-emerald-500 to-emerald-700' : 'from-blue-500 to-blue-700'} flex items-center justify-center shadow-sm`}>
                                        {isHse ? <ShieldCheck className="w-5 h-5 text-white" /> : <HardHat className="w-5 h-5 text-white" />}
                                    </div>
                                )
                            ) : (
                                /* Expanded: full logo image */
                                <img
                                    src="https://otkrfgysfhohefploofg.supabase.co/storage/v1/object/public/static-assets/GefAS_Logo.png"
                                    alt={companyName || 'GefAS mbH'}
                                    className="h-10 object-contain"
                                />
                            )}
                        </div>
                    )}
                </div>

                {/* ── NAVIGATION ── */}
                <div className={`flex-1 overflow-y-auto ${c ? 'px-1.5 py-2' : 'px-3 py-2'} space-y-0.5`}>
                    {/* In der App steht fuer ausgeblendete Module der Platzhalter
                        „Nur im Browser" — deren Abschnittsmenue gehoert dann nicht hierher. */}
                    {view === 'editor' && zeige(view) && renderEditorMenu()}
                    {view === 'module-sigeko-inspection' && zeige(view) && renderSiGeKoMenu()}
                    {view === 'module-unterlage' && zeige(view) && renderUnterlageMenu()}
                    {view === 'module-vorank' && zeige(view) && renderVorankMenu()}
                    {view === 'project-hub' && zeige(view) && renderProjectHubMenu()}
                    {HAUPTMENUE_ANSICHTEN.includes(view) && renderDashboardMenu()}
                </div>

                {/* ── FOOTER ── */}
                <div className={`shrink-0 border-t border-slate-200/60 ${c ? 'px-1.5 py-2' : 'px-3 py-3'} space-y-2`}>
                    {/* Collapse toggle (desktop only) */}
                    {!isMobile && (
                        <button
                            onClick={() => (glass ? setRailOpen(!railOpen) : setCollapsed(!collapsed))}
                            data-tutorial="sidebar.einklappen"
                            className={`w-full flex items-center justify-center gap-2 py-1.5 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-100/80 transition-colors ${c ? '' : 'px-3'}`}
                        >
                            {c ? <PanelLeftOpen className="w-4 h-4" /> : <><PanelLeftClose className="w-4 h-4" /><span className="text-[11px] font-bold">Einklappen</span></>}
                        </button>
                    )}

                    {/* User — öffnet das Konto-Menü */}
                    <div ref={kontoRef} className="relative group">
                        {kontoOffen && (
                            <div
                                id={kontoMenueId}
                                role="menu"
                                aria-label="Konto"
                                onKeyDown={kontoPfeile}
                                className={`absolute z-[110] rounded-xl bg-white p-1.5 shadow-lg ring-1 ring-slate-200 animate-scale-in
                                    ${c ? 'left-full bottom-0 ml-3 w-56 origin-bottom-left' : 'bottom-full inset-x-0 mb-2 origin-bottom'}`}
                            >
                                {/* Eingeklappt fehlt der Name neben dem Avatar — hier steht er. */}
                                {c && (
                                    <div className="px-2.5 pt-1 pb-2 mb-1 border-b border-slate-100">
                                        <p className="text-xs font-bold text-slate-700 truncate">{userProfile?.first_name ? `${userProfile.first_name} ${userProfile.last_name}` : 'Benutzer'}</p>
                                        <p className="text-[11px] text-slate-400 truncate">{userProfile?.email}</p>
                                    </div>
                                )}
                                <KontoEintrag icon={<UserCog className="w-4 h-4" />} label="Profil & Einstellungen" onClick={kontoWahl(onOpenProfile)} />
                                {appEintragSichtbar && (
                                    <KontoEintrag icon={<Smartphone className="w-4 h-4" />} label={variante ? 'App-Info' : 'App herunterladen'} isActive={appSeiteAktiv} onClick={kontoWahl(() => onOpenAppSeite?.())} />
                                )}
                                {zeige('lernzentrum') && (
                                    <KontoEintrag icon={<GraduationCap className="w-4 h-4" />} label="Lernzentrum & Hilfe" isActive={aktiv('lernzentrum')} onClick={kontoWahl(() => nav('lernzentrum'))} />
                                )}
                                <div role="separator" className="my-1 mx-1 border-t border-slate-100" />
                                <KontoEintrag icon={<LogOut className="w-4 h-4" />} label="Abmelden" gefahr onClick={kontoWahl(onLogout)} />
                            </div>
                        )}
                        <button
                            ref={kontoKnopfRef}
                            type="button"
                            onClick={() => setKontoOffen(offen => !offen)}
                            data-tutorial="sidebar.profil"
                            aria-haspopup="menu"
                            aria-expanded={kontoOffen}
                            aria-controls={kontoOffen ? kontoMenueId : undefined}
                            className={`w-full flex items-center gap-2.5 rounded-xl transition-all hover:bg-slate-100/80 ${kontoOffen ? 'bg-slate-100/80' : ''} ${c ? 'justify-center p-2' : 'px-3 py-2'}`}
                        >
                            <div className="relative shrink-0">
                                <div className={`rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center text-white font-bold shadow-sm ${c ? 'w-8 h-8 text-xs' : 'w-8 h-8 text-xs'}`}>
                                    {(userProfile?.first_name?.[0] || 'U').toUpperCase()}
                                </div>
                                {saveStatus === 'saving' && <div className="absolute inset-0 -m-0.5 rounded-full border-2 border-blue-400 border-t-transparent animate-spin" />}
                                {(saveStatus === 'error' || saveStatus === 'storage-error' || saveStatus === 'auth-required') && <div className="absolute inset-0 -m-0.5 rounded-full border-2 border-red-400" />}
                                {(saveStatus === 'queued' || saveStatus === 'offline' || saveStatus === 'read-only') && <div className="absolute inset-0 -m-0.5 rounded-full border-2 border-amber-400" />}
                                {saveStatus === 'saved' && <div className="absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 bg-emerald-500 rounded-full border-2 border-white" />}
                            </div>
                            {!c && (
                                <div className="min-w-0 text-left">
                                    <p className="text-xs font-bold text-slate-700 truncate">{userProfile?.first_name ? `${userProfile.first_name} ${userProfile.last_name}` : 'Benutzer'}</p>
                                    <p data-tutorial="sperre.speicherstand" className={`text-[11px] truncate ${saveStatus === 'saving' ? 'text-blue-500' : (saveStatus === 'error' || saveStatus === 'storage-error' || saveStatus === 'auth-required') ? 'text-red-500' : (saveStatus === 'queued' || saveStatus === 'offline' || saveStatus === 'read-only') ? 'text-amber-600' : 'text-slate-400'}`}>
                                        {saveStatus === 'read-only' ? 'Nicht gespeichert (Nur-Ansicht)'
                                            : saveStatus === 'saving' ? 'Speichert...'
                                            : saveStatus === 'error' ? 'Fehler'
                                            : saveStatus === 'queued' ? 'Wartet auf Sync'
                                            : saveStatus === 'offline' ? 'Offline – lokal gesichert'
                                            : saveStatus === 'auth-required' ? 'Anmeldung erforderlich'
                                            : saveStatus === 'storage-error' ? 'Lokaler Speicher voll'
                                            : userProfile?.email}
                                    </p>
                                </div>
                            )}
                        </button>
                        {/* Tooltip nur bei geschlossenem Menü — es läge sonst darüber. */}
                        {c && !kontoOffen && (
                            <div className="absolute left-full top-1/2 -translate-y-1/2 ml-3 px-2.5 py-1.5 bg-slate-800 text-white text-xs font-bold rounded-lg whitespace-nowrap opacity-0 pointer-events-none group-hover:opacity-100 transition-opacity z-[100] shadow-lg">
                                {userProfile?.first_name} {userProfile?.last_name}
                                <div className="absolute right-full top-1/2 -translate-y-1/2 border-4 border-transparent border-r-slate-800" />
                            </div>
                        )}
                    </div>

                    {/* Build-Stempel — zeigt, welcher Stand wirklich laeuft */}
                    <p
                        className="text-[10px] font-mono text-slate-400 text-center leading-tight truncate select-all"
                        title={`${BUILD_STAMP} — bei Abweichung vom erwarteten Stand ist das Deployment veraltet oder der Browser-Cache haelt ein altes Bundle`}
                    >
                        {c ? BUILD_INFO.commit : BUILD_STAMP}
                    </p>
                </div>
            </aside>
        </>
    );
};
